import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save, Loader2, Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { workCenterCostingService } from '../../services/workCenterCostingService';
import { workCenterService } from '../../services/workCenterService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import AddWorkCentreModal from './AddWorkCentreModal';

// =============================================================================
// Cost / Work Centres — the costing model.
//
// The user captures FOUR numbers. Everything else is derived from data that
// already exists elsewhere in the budget:
//
//   CAPTURED (white cells)              DERIVED (grey / green cells)
//   ----------------------------------  ------------------------------------
//   Rates tab   Setup Rate  R/hr        Volume   <- Production Volumes
//               Run Rate    R/hr        Batch    <- Bill of Materials
//   Routing tab Setup Time  hrs/batch   Batches  = volume / batch
//               Run Time    hrs/unit    Hours    = setup x batches + run x volume
//                                       Cost     = hours x the two rates
//
//   cost/unit = run_time x run_rate + (setup_time x setup_rate) / batch
//
// Setup and run are priced separately because a changeover is not the same
// cost as a machine running — no heavy utilities while you are setting up.
//
// An item that is NOT batch-controlled on the BOM has no batch size, so its
// setup is charged ONCE for the period rather than being dropped silently.
// =============================================================================

const DEFAULT_STOCK = '*'; // the work centre's default routing row

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hrs = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const unit4 = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const unit5 = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 5, maximumFractionDigits: 5 });
const int0 = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const int1 = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

const T_CALC = { bgCell: 'rgba(100, 116, 139, 0.10)' };
const T_RESULT = { bgCell: 'rgba(16, 185, 129, 0.12)', textDark: '#047857', baseFontStyle: '600 12px' };
const T_GROUP = { bgCell: 'rgba(100, 116, 139, 0.22)', baseFontStyle: '600 12px' };
const T_MUTED = { textDark: '#94a3b8' };
const T_INHERIT = { textDark: '#94a3b8', bgCell: 'rgba(100, 116, 139, 0.05)' };
const T_SETUP = { bgCell: 'rgba(245, 158, 11, 0.10)' };
const T_RUN = { bgCell: 'rgba(14, 165, 233, 0.10)' };
const B_DEFAULT = { bgBubble: 'rgba(100, 116, 139, 0.12)', textBubble: '#64748b' };
const B_NOBATCH = { bgBubble: 'rgba(245, 158, 11, 0.18)', textBubble: '#b45309' };

// Rates captures the two rates and nothing else — the derived hours and cost
// belong on Routing, where the volumes and batches that produce them are
// visible. `grow` lets the text columns stretch so the table fills its width.
const RATE_COLUMNS = [
  { id: 'wc',        title: 'Work Centre',    width: 280, grow: 3 },
  { id: 'driver',    title: 'Driver',         width: 150, grow: 1 },
  { id: 'setupRate', title: 'Setup Rate /hr', width: 150 },
  { id: 'runRate',   title: 'Run Rate /hr',   width: 150 },
  { id: 'del',       title: 'Delete',         width: 70 },
];
const RATE_IDS = RATE_COLUMNS.map((c) => c.id);

const ROUTE_COLUMNS = [
  { id: 'item',     title: 'Work Centre / Item', width: 290, grow: 1 },
  { id: 'flag',     title: '',                 width: 90 },
  { id: 'setup',    title: 'Setup Hrs /batch', width: 130 },
  { id: 'run',      title: 'Run Hrs /unit',    width: 125 },
  { id: 'batch',    title: 'Batch (BOM)',      width: 110 },
  { id: 'volume',   title: 'Volume',           width: 115 },
  { id: 'batches',  title: 'Batches',          width: 105 },
  { id: 'hours',    title: 'Hours',            width: 110 },
  { id: 'costUnit', title: 'Cost / Unit',      width: 115 },
];
const ROUTE_IDS = ROUTE_COLUMNS.map((c) => c.id);

const keyOf = (wc, stock) => `${wc}|${stock}`;

const WorkCentreCosting = () => {
  const { selectedVersion, selectedSite } = useAppContext();

  const [tab, setTab] = useState('rates');
  const [centers, setCenters] = useState([]);
  const [routing, setRouting] = useState({});   // { "wc|stock": { setup_time, run_time } }
  const [items, setItems] = useState([]);       // [{ stock_code, description, batch_size, volume }]
  const [dirtyRates, setDirtyRates] = useState({});   // { wcCode: true }
  const [dirtyRouting, setDirtyRouting] = useState({}); // { "wc|stock": true }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const ready = !!selectedVersion && !!selectedSite;

  const load = useCallback(async () => {
    if (!ready) { setCenters([]); setItems([]); setRouting({}); return; }
    setLoading(true);
    setError('');
    try {
      const res = await workCenterCostingService.getCosting();
      if (res?.success) {
        setCenters(res.data.centers || []);
        setItems(res.data.items || []);
        const map = {};
        for (const r of res.data.routing || []) {
          map[keyOf(r.work_center_code, r.stock_code)] = { setup_time: r.setup_time, run_time: r.run_time };
        }
        setRouting(map);
        setDirtyRates({});
        setDirtyRouting({});
      } else {
        setError(res?.message || 'Failed to load work-centre costing.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load work-centre costing.');
    } finally {
      setLoading(false);
    }
  }, [ready]);

  useEffect(() => { load(); }, [load]);

  // ---- Derivation ----------------------------------------------------------
  // An item with no row of its own uses the work centre's default; an item with
  // no batch size gets ONE setup for the period (see the header note).
  const timesFor = useCallback((wc, stock) => {
    const own = routing[keyOf(wc, stock)];
    if (own) return { ...own, inherited: false };
    const def = routing[keyOf(wc, DEFAULT_STOCK)] || { setup_time: 0, run_time: 0 };
    return { ...def, inherited: true };
  }, [routing]);

  const batchesOf = useCallback((item) => {
    const b = Number(item.batch_size) || 0;
    const v = Number(item.volume) || 0;
    return b > 0 ? v / b : (v > 0 ? 1 : 0);
  }, []);

  const lineOf = useCallback((wc, item) => {
    const t = timesFor(wc.code, item.stock_code);
    const batches = batchesOf(item);
    const volume = Number(item.volume) || 0;
    const setupHrs = (Number(t.setup_time) || 0) * batches;
    const runHrs = (Number(t.run_time) || 0) * volume;
    const cost = setupHrs * (Number(wc.setup_rate) || 0) + runHrs * (Number(wc.run_rate) || 0);
    return {
      ...t, batches, volume, setupHrs, runHrs, cost,
      costUnit: volume > 0 ? cost / volume : 0,
      noBatch: !(Number(item.batch_size) > 0),
    };
  }, [timesFor, batchesOf]);

  // Hours and cost per work centre, summed over the items it routes.
  const byCentre = useMemo(() => {
    const out = {};
    for (const c of centers) {
      let setupHrs = 0, runHrs = 0, cost = 0;
      for (const it of items) {
        const l = lineOf(c, it);
        setupHrs += l.setupHrs; runHrs += l.runHrs; cost += l.cost;
      }
      out[c.code] = { setupHrs, runHrs, cost };
    }
    return out;
  }, [centers, items, lineOf]);

  // ---- Rates grid ----------------------------------------------------------
  const centersRef = useRef(centers);
  centersRef.current = centers;

  const getRateCell = useCallback(([col, row]) => {
    const c = centersRef.current[row];
    const id = RATE_IDS[col];
    if (!c) return gcell.text('', { readonly: true });

    switch (id) {
      case 'wc': return gcell.text(`${c.code} · ${c.description}`, { readonly: true });
      case 'driver': return gcell.text(c.driver_uom || '—', { readonly: true, theme: T_MUTED });
      case 'setupRate': return gcell.number(c.setup_rate, { display: money(c.setup_rate), theme: T_SETUP });
      case 'runRate': return gcell.number(c.run_rate, { display: money(c.run_rate), theme: T_RUN });
      case 'del': return gcell.text('✕', { readonly: true, theme: T_MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, []);

  const onRateEdited = useCallback(([col, row], v) => {
    const id = RATE_IDS[col];
    if (id !== 'setupRate' && id !== 'runRate') return;
    const field = id === 'setupRate' ? 'setup_rate' : 'run_rate';
    setCenters((prev) => prev.map((c, i) => {
      if (i !== row) return c;
      setDirtyRates((d) => ({ ...d, [c.code]: true }));
      return { ...c, [field]: Number(v.data) || 0 };
    }));
  }, []);

  // Deleting a work centre cascades its routing and its posting mapping, so it
  // is worth being explicit about that in the confirm.
  const onRateClicked = useCallback(async ([col, row]) => {
    if (RATE_IDS[col] !== 'del') return;
    const c = centersRef.current[row];
    if (!c) return;
    const ok = window.confirm(
      `Delete work centre ${c.code} · ${c.description}?\n\n`
      + 'Its routing times and its cost centre / GL mapping go with it. This cannot be undone.'
    );
    if (!ok) return;
    try {
      const res = await workCenterService.deleteWorkCenter(c.code);
      // The endpoint reports success even when nothing matched, so check the
      // count — otherwise a no-op looks identical to a delete.
      if (res?.success && res.deletedCount === 0) {
        setError(`${c.code} was not found for this site and version — nothing deleted.`);
      } else if (res?.success) {
        setError('');
      } else {
        setError(res?.message || 'Could not delete the work centre.');
      }
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not delete the work centre.');
    }
  }, [load]);

  // ---- Routing grid --------------------------------------------------------
  const routeRows = useMemo(() => {
    const out = [];
    for (const c of centers) {
      out.push({ kind: 'group', wc: c });
      out.push({ kind: 'default', wc: c });
      for (const it of items) out.push({ kind: 'item', wc: c, item: it });
    }
    return out;
  }, [centers, items]);

  const routeRef = useRef(routeRows);
  routeRef.current = routeRows;

  const getRouteCell = useCallback(([col, row]) => {
    const R = routeRef.current[row];
    const id = ROUTE_IDS[col];
    if (!R) return gcell.text('', { readonly: true });
    const c = R.wc;

    if (R.kind === 'group') {
      const agg = byCentre[c.code] || { setupHrs: 0, runHrs: 0, cost: 0 };
      if (id === 'item') return gcell.text(`${c.code} · ${c.description}`, { readonly: true, theme: T_GROUP });
      if (id === 'flag') return gcell.text(`${money(c.setup_rate)} / ${money(c.run_rate)}`, { readonly: true, theme: T_GROUP });
      if (id === 'hours') return gcell.number(agg.setupHrs + agg.runHrs, { readonly: true, display: hrs(agg.setupHrs + agg.runHrs), theme: T_GROUP });
      return gcell.text('', { readonly: true, theme: T_GROUP });
    }

    if (R.kind === 'default') {
      const t = routing[keyOf(c.code, DEFAULT_STOCK)] || { setup_time: 0, run_time: 0 };
      switch (id) {
        case 'item': return gcell.text('   Default — all items', { readonly: true, theme: T_MUTED });
        case 'flag': return gcell.bubble(['template'], { theme: B_DEFAULT });
        case 'setup': return gcell.number(t.setup_time, { display: unit4(t.setup_time), theme: T_SETUP });
        case 'run': return gcell.number(t.run_time, { display: unit5(t.run_time), theme: T_RUN });
        default: return gcell.text('—', { readonly: true, theme: T_MUTED });
      }
    }

    const it = R.item;
    const l = lineOf(c, it);
    const T = l.inherited ? T_INHERIT : undefined;

    switch (id) {
      case 'item': return gcell.text(`   ${it.stock_code} · ${it.description}`, { readonly: true });
      case 'flag': return l.inherited ? gcell.bubble(['default'], { theme: B_DEFAULT }) : gcell.text('', { readonly: true });
      case 'setup': return gcell.number(l.setup_time, { display: unit4(l.setup_time), theme: T || T_SETUP });
      case 'run': return gcell.number(l.run_time, { display: unit5(l.run_time), theme: T || T_RUN });
      case 'batch':
        // No batch control on the BOM -> one setup for the period.
        return l.noBatch
          ? gcell.bubble(['1 setup'], { theme: B_NOBATCH })
          : gcell.number(it.batch_size, { readonly: true, display: int0(it.batch_size), theme: T_CALC });
      case 'volume': return gcell.number(it.volume, { readonly: true, display: int0(it.volume), theme: T_CALC });
      case 'batches': return gcell.number(l.batches, { readonly: true, display: int1(l.batches), theme: T_CALC });
      case 'hours': return gcell.number(l.setupHrs + l.runHrs, { readonly: true, display: hrs(l.setupHrs + l.runHrs), theme: T_CALC });
      case 'costUnit': return gcell.number(l.costUnit, { readonly: true, display: unit4(l.costUnit), theme: T_RESULT });
      default: return gcell.text('', { readonly: true });
    }
  }, [byCentre, routing, lineOf]);

  // Typing a time on an inherited item is what gives it a row of its own.
  const onRouteEdited = useCallback(([col, row], v) => {
    const R = routeRef.current[row];
    if (!R || R.kind === 'group') return;
    const id = ROUTE_IDS[col];
    if (id !== 'setup' && id !== 'run') return;

    const stock = R.kind === 'default' ? DEFAULT_STOCK : R.item.stock_code;
    const k = keyOf(R.wc.code, stock);
    const field = id === 'setup' ? 'setup_time' : 'run_time';

    setRouting((prev) => {
      const base = prev[k] || (R.kind === 'item'
        ? (prev[keyOf(R.wc.code, DEFAULT_STOCK)] || { setup_time: 0, run_time: 0 })
        : { setup_time: 0, run_time: 0 });
      return { ...prev, [k]: { ...base, [field]: Number(v.data) || 0 } };
    });
    setDirtyRouting((d) => ({ ...d, [k]: true }));
  }, []);

  // Click the chip to detach an item's override and fall back to the default.
  const onRouteClicked = useCallback(async ([col, row]) => {
    const R = routeRef.current[row];
    if (!R || R.kind !== 'item' || ROUTE_IDS[col] !== 'flag') return;
    const k = keyOf(R.wc.code, R.item.stock_code);
    if (!routing[k]) return; // already on the default

    setRouting((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setDirtyRouting((d) => {
      const next = { ...d };
      delete next[k];
      return next;
    });
    try {
      await workCenterCostingService.deleteRouting(R.wc.code, R.item.stock_code);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not remove the override.');
      load();
    }
  }, [routing, load]);

  // ---- Save ----------------------------------------------------------------
  const hasChanges = Object.keys(dirtyRates).length > 0 || Object.keys(dirtyRouting).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    setError('');
    try {
      const res = await workCenterCostingService.saveCosting({
        centers: centers
          .filter((c) => dirtyRates[c.code])
          .map((c) => ({ code: c.code, setup_rate: c.setup_rate, run_rate: c.run_rate })),
        routing: Object.keys(dirtyRouting).map((k) => {
          const [wc, stock] = k.split('|');
          return {
            work_center_code: wc, stock_code: stock,
            setup_time: routing[k]?.setup_time || 0,
            run_time: routing[k]?.run_time || 0,
          };
        }),
      });
      if (res?.success) { setDirtyRates({}); setDirtyRouting({}); }
      else setError(res?.message || 'Save failed.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    let setupHrs = 0, runHrs = 0, cost = 0;
    for (const c of centers) {
      const a = byCentre[c.code] || { setupHrs: 0, runHrs: 0, cost: 0 };
      setupHrs += a.setupHrs; runHrs += a.runHrs; cost += a.cost;
    }
    return { setupHrs, runHrs, cost };
  }, [centers, byCentre]);

  const tabClass = (id) =>
    `px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
      tab === id
        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100">
            Cost / Work Centres
          </h1>
          <div className="flex items-center gap-1.5">
            <button type="button" className={tabClass('rates')} onClick={() => setTab('rates')}>Rates</button>
            <button type="button" className={tabClass('routing')} onClick={() => setTab('routing')}>Routing</button>
          </div>
          {error && <span className="text-[11px] text-red-600 dark:text-red-400">{error}</span>}

          <div className="ml-auto flex items-end gap-4">
            <Stat label="Setup hrs" value={hrs(totals.setupHrs)} />
            <Stat label="Run hrs" value={hrs(totals.runHrs)} />
            <Stat label="Recovered" value={money(totals.cost)} />
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              disabled={!ready}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <Plus className="w-3 h-3" /> Add work centre
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md text-white shadow-sm transition-colors ${
                hasChanges && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <p className="m-0 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          {tab === 'rates'
            ? 'Two rates per work centre. Setup is a changeover — crew and fixed overhead, no heavy utilities. Run is the machine working. The hours these earn are on the Routing tab.'
            : 'You type two times per item. Volume comes from Production Volumes and batch size from the Bill of Materials — Cost / Unit = run × run rate + (setup × setup rate) ÷ batch.'}
        </p>
      </div>

      {/* Height differs by tab, and the two must agree with the grid inside:
          Rates uses fitHeight, so the panel is max-h-full and shrink-wraps its
          handful of rows. Routing is a long grouped list that fills the screen
          and scrolls internally, so it needs a real h-full to size against —
          a fitHeight-less grid asks for height:100%, which collapses to nothing
          inside an auto-height parent. */}
      <div className="flex-1 min-h-0 px-6 pb-4">
        <div className={`${tab === 'rates' ? 'max-h-full' : 'h-full'} max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden`}>
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">
              {!selectedVersion
                ? 'Select a budget version in the top bar to begin.'
                : 'Select a site — work centres are captured per site.'}
            </div>
          ) : loading ? (
            <div className="p-10 text-center text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : centers.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              No work centres for this site and version yet.<br />
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <Plus className="w-3.5 h-3.5" /> Add the first work centre
              </button>
            </div>
          ) : tab === 'rates' ? (
            <MantleDataGrid
              gridId="wcc-rates"
              fitHeight
              columns={RATE_COLUMNS}
              rowCount={centers.length}
              getCellContent={getRateCell}
              onCellEdited={onRateEdited}
              onCellClicked={onRateClicked}
              freezeColumns={1}
              hint="Amber = setup (changeover: crew and fixed overhead). Blue = run (machine active, including utilities). ✕ deletes a work centre. Hours and cost are on the Routing tab."
            />
          ) : (
            <MantleDataGrid
              gridId="wcc-routing"
              columns={ROUTE_COLUMNS}
              rowCount={routeRows.length}
              getCellContent={getRouteCell}
              onCellEdited={onRouteEdited}
              onCellClicked={onRouteClicked}
              freezeColumns={1}
              hint="Each work centre has one Default row. An item keeps the grey “default” chip until you type a time on it — click the chip to drop the override. An amber “1 setup” chip means the item has no batch size on the BOM, so setup is charged once."
            />
          )}
        </div>
      </div>

      {addOpen && (
        <AddWorkCentreModal
          existingCodes={centers.map((c) => c.code)}
          onAdded={() => { setAddOpen(false); load(); }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-medium leading-none text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</span>
    <span className="text-sm font-bold font-mono text-slate-800 dark:text-slate-100">{value}</span>
  </div>
);

export default WorkCentreCosting;
